import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import RefreshIcon from "@mui/icons-material/Refresh";
import axiosInstance from "../utils/axiosInstance";
import { logger } from "../utils/logger";
import { useSelector } from "react-redux";

const TYPE_META = {
  donors: { label: "Donors", color: "primary", icon: <PeopleAltIcon fontSize="small" /> },
  donations: { label: "Donations", color: "success", icon: <VolunteerActivismIcon fontSize="small" /> },
};

const DAY_OPTIONS = [
  { label: "Last 15 days", value: 15 },
  { label: "Last 30 days", value: 30 },
  { label: "All", value: 0 },
];

const formatDisplay = (isoString) => {
  if (!isoString) return "-";
  return new Date(isoString).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const DataExportPage = () => {
  const { user } = useSelector((state) => state.auth);

  const [days, setDays] = useState(15);
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState("");
  const [downloadingFile, setDownloadingFile] = useState("");
  const [deletingFile, setDeletingFile] = useState("");

  const fetchFiles = useCallback(async () => {
    setFilesLoading(true);
    setFilesError("");
    try {
      const res = await axiosInstance.get("/api/data-export/files", {
        params: days > 0 ? { days } : {},
      });
      setFiles(res.data?.files || []);
    } catch (err) {
      logger.error("Failed to fetch export files", { error: err });
      setFilesError("Failed to load file list. Please refresh.");
    } finally {
      setFilesLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const handleDownload = async (filename) => {
    setDownloadingFile(filename);
    try {
      const res = await axiosInstance.get(
        `/api/data-export/download/${encodeURIComponent(filename)}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      logger.info(`Downloaded: ${filename}`, { username: user?.username, role: user?.role });
    } catch (err) {
      logger.error(`Download failed: ${filename}`, { error: err });
    } finally {
      setDownloadingFile("");
    }
  };

  const handleDelete = async (filename) => {
    setDeletingFile(filename);
    try {
      await axiosInstance.delete(
        `/api/data-export/files/${encodeURIComponent(filename)}`
      );
      setFiles((prev) => prev.filter((f) => f.filename !== filename));
      logger.info(`Deleted: ${filename}`, { username: user?.username, role: user?.role });
    } catch (err) {
      logger.error(`Delete failed: ${filename}`, { error: err });
    } finally {
      setDeletingFile("");
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", mt: 2 }}>
      <Typography variant="h5" fontWeight={600} mb={0.5}>
        Data Export
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Donor and donation snapshots are generated automatically every day at
        2:00 AM and kept for 30 days. Download any file below.
      </Typography>

      {/* ── Controls row ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <ToggleButtonGroup
          value={days}
          exclusive
          size="small"
          onChange={(_, val) => { if (val !== null) setDays(val); }}
        >
          {DAY_OPTIONS.map((opt) => (
            <ToggleButton key={opt.value} value={opt.value} sx={{ textTransform: "none" }}>
              {opt.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Tooltip title="Refresh">
          <span>
            <IconButton size="small" onClick={fetchFiles} disabled={filesLoading}>
              {filesLoading ? <CircularProgress size={18} /> : <RefreshIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {filesError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {filesError}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>File Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Generated At</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Size</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filesLoading && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {!filesLoading && files.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  No export files found. Files are generated automatically at 2:00 AM daily.
                </TableCell>
              </TableRow>
            )}
            {!filesLoading &&
              files.map((file) => {
                const meta = TYPE_META[file.type] || { label: file.type, color: "default", icon: null };
                const isDownloading = downloadingFile === file.filename;
                const isDeleting = deletingFile === file.filename;
                return (
                  <TableRow key={file.filename} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{file.date || "-"}</TableCell>
                    <TableCell>
                      <Chip
                        icon={meta.icon}
                        label={meta.label}
                        color={meta.color}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.78rem", color: "text.secondary" }}>
                      {file.filename}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.8rem" }}>
                      {formatDisplay(file.generatedAt)}
                    </TableCell>
                    <TableCell>{file.sizeKB} KB</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                        <Tooltip title="Download">
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              disabled={isDownloading || isDeleting}
                              onClick={() => handleDownload(file.filename)}
                            >
                              {isDownloading
                                ? <CircularProgress size={16} />
                                : <DownloadIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete from server">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={isDownloading || isDeleting}
                              onClick={() => handleDelete(file.filename)}
                            >
                              {isDeleting
                                ? <CircularProgress size={16} />
                                : <DeleteIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default DataExportPage;
